import { FC } from "react";
import {
    Button,
    Card,
    CardBody,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    VStack,
} from "@chakra-ui/react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useToast } from "@am-tax/fe-core";
import { CandidateQualification, displayQualification, EntityCandidateForUpdate } from "../../api/models/EntityCandidate";
import { useCandidatePatch } from "../../api/hooks/useCandidatePatch";
import { buildJsonPatch } from "../../utils/buildJsonPatch";
import { useCandidateQualifications } from "../../api/hooks/useCandidateQualifications";
import { QualificationCheckboxes } from "../CandidateDetailsPage/QualificationCheckboxes";
import globalEvents from "../../utils/globalEvents";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    candidate: Partial<EntityCandidateForUpdate>;
    cId: string | undefined;
    eId: string | undefined;
    candidateId: string | undefined;
}

type FormValues = Pick<EntityCandidateForUpdate, "selectedQualification" | "qualifications">;

export const CandidateEligibilityModal: FC<Props> = ({ isOpen, onClose, candidate, cId, eId, candidateId }) => {
    const toast = useToast();
    const patchCandidate = useCandidatePatch();

    const {
        register,
        handleSubmit,
        formState: { dirtyFields },
        reset,
    } = useForm<FormValues>({ values: candidate as FormValues });

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit: SubmitHandler<FormValues> = async data => {
        const ops = buildJsonPatch(dirtyFields as Record<string, unknown>, data as Record<string, unknown>);
        if (ops.length > 0) {
            await patchCandidate.mutateAsync({ clientId: cId, entityId: eId, candidateId, patch: ops });
        }
        await globalEvents.callAsyncEvent("updateCandidateQualifications");
        toast({ title: "Saved", status: "success", duration: 3000, isClosable: true });
        handleClose();
    };

    const qualificationsQuery = useCandidateQualifications(candidateId, cId, eId);
    const qualifiedSet = new Set((qualificationsQuery.data ?? []).map(q => q.value as string));

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size={"2xl"}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Edit Eligibility</ModalHeader>
                <ModalCloseButton />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        <fieldset disabled={patchCandidate.isLoading}>
                            <VStack gap={"1rem"} alignItems={"start"}>
                                <FormControl isReadOnly>
                                    <FormLabel>Questionnaire Completion Date:</FormLabel>
                                    <Input value={candidate.questionnaireCompletedOn ?? ""} readOnly variant={"filled"} />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Target Qualification:</FormLabel>
                                    <Select placeholder={"Select a Qualification"} {...register("selectedQualification")}>
                                        {qualifiedSet.size > 0 && (
                                            <optgroup label="Qualified">
                                                {[...qualifiedSet].map(q => (
                                                    <option key={q} value={q}>
                                                        {displayQualification(q as CandidateQualification)}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        )}
                                        <optgroup label="All Other Qualifications">
                                            {Object.values(CandidateQualification)
                                                .filter(q => !qualifiedSet.has(q))
                                                .map(q => (
                                                    <option key={q} value={q}>
                                                        {displayQualification(q)}
                                                    </option>
                                                ))}
                                        </optgroup>
                                    </Select>
                                </FormControl>

                                <Card width={"100%"}>
                                    <CardBody>
                                        <VStack spacing=".5em" justifyContent="left" alignItems={"stretch"}>
                                            <QualificationCheckboxes clientId={cId} candidateId={candidateId} entityId={eId} tenantId={cId} />
                                        </VStack>
                                    </CardBody>
                                </Card>
                            </VStack>
                        </fieldset>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            type="submit"
                            variant={"primary"}
                            mr={3}
                            isLoading={patchCandidate.isLoading}
                            isDisabled={patchCandidate.isLoading}
                            loadingText={"Saving"}
                        >
                            Save
                        </Button>
                        <Button variant="ghost" onClick={handleClose}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    );
};
